import { cn, UzButton } from '@utazzo/ui';
import { ArrowDown, ArrowUp, ArrowUpDown, Maximize2, Minimize2 } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Report building blocks styled like the rest of the admin CRM: token-based
 * card (`bg-card` / `border-border`), muted uppercase header row, hairline
 * cell borders and a bold totals row. Tables support click-to-sort headers,
 * a sticky header row + first column inside a scrollable card, and shimmer
 * skeletons while loading. `data-report-title` on the card root feeds the
 * per-tab CSV export.
 */

interface ReportSectionProps {
  title: string;
  /** Optional right-aligned header content (filters, buttons). */
  actions?: React.ReactNode;
  /**
   * Optional controls (e.g. radio filters) rendered ABOVE the scroll box and
   * OUTSIDE it, so they stay put when the table is scrolled horizontally.
   */
  toolbar?: React.ReactNode;
  /**
   * Overrides the surrounding surface's default. Left undefined, the section
   * does whatever the page it sits on does.
   */
  expandable?: boolean;
  children: React.ReactNode;
}

/**
 * What a `ReportSection` is being drawn on.
 *
 * `card` is the page: a bordered card with its own header and its own 70vh
 * scroll box. `sheet` is the workbook overlay, where the surrounding sheet is
 * already the scroll container and owns the whole viewport - a card inside it
 * would put a second frame and a second scrollbar around the table, and pin the
 * header to the card rather than to the screen.
 *
 * A context rather than a prop because the tables that render a ReportSection
 * sit three or four components below whatever decides the surface, and every
 * one of them would otherwise have to pass the flag through untouched.
 */
interface ReportSurface {
  /** A page card, or the Marketing report's full-screen scroll. */
  surface: 'card' | 'sheet';
  /** Whether a card offers the full-screen toggle in its header. */
  expandable: boolean;
  /**
   * Cards cap their table at 70vh with an inner scroll by default. `false`
   * lets every card run its full height and leaves the page as the only
   * vertical scroll (the Marketing report, 2026-11-05); wide tables still
   * scroll sideways inside the card.
   */
  innerScroll?: boolean;
}

/**
 * Defaults deliberately match how every OTHER report page already behaved:
 * a plain card with no expand button. Only the Marketing report opts in, by
 * wrapping its content in a provider, so this file can grow features for that
 * report without changing the Daily Tracker or the Ticketing reports.
 */
export const ReportSurfaceContext = React.createContext<ReportSurface>({
  surface: 'card',
  expandable: false,
});

/**
 * A report card. On a surface that asks for it, its header carries a
 * full-screen toggle.
 *
 * These tables are wide - a dozen month columns, or a metric column plus every
 * channel - and in the page they sit in a 70vh box with the sidebar and the
 * page chrome taking the rest. Expanded, the card covers the viewport and the
 * scroll box takes all of it, so a table can be read without scrolling in two
 * directions at once. Escape closes it, and the page behind is held still
 * meanwhile so it does not drift under the overlay.
 */
export const ReportSection = ({
  title,
  actions,
  toolbar,
  expandable,
  children,
}: ReportSectionProps): React.JSX.Element => {
  const {
    surface,
    expandable: surfaceExpandable,
    innerScroll = true,
  } = React.useContext(ReportSurfaceContext);
  const showExpand = expandable ?? surfaceExpandable;
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [expanded]);

  if (surface === 'sheet') {
    // No card, no inner scroll box: the sheet scrolls, so the table's sticky
    // header pins to the top of the screen instead of to a box inside it.
    return (
      <section data-report-title={title} className="space-y-2">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {title}
          </h2>
          {actions}
        </header>
        {toolbar}
        {children}
      </section>
    );
  }

  return (
    <div
      data-report-title={title}
      className={cn(
        'border-border bg-card border shadow-sm',
        expanded ? 'fixed inset-0 z-60 flex flex-col rounded-none' : 'rounded-xl',
      )}
    >
      {/* Tinted band + accent rule so each section title reads as a heading at a
          glance when several tables stack on one tab. */}
      <header
        className={cn(
          'border-border bg-muted/50 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3',
          !expanded && 'rounded-t-xl',
        )}
      >
        <h2 className="text-foreground border-primary border-l-2 pl-2.5 text-sm font-semibold">
          {title}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {showExpand && (
            <UzButton
              variant="outline"
              size="sm"
              onClick={() => setExpanded((prev) => !prev)}
              title={expanded ? 'Exit full screen (Esc)' : 'Expand to full screen'}
              aria-label={expanded ? `Exit full screen: ${title}` : `Expand ${title}`}
            >
              {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </UzButton>
          )}
        </div>
      </header>
      {/* Padding lives on this OUTER wrapper, never on the scroll box. A scroll
          container with its own padding sticks `left-0`/`top-0` cells to the
          content-box edge, leaving the padding as a gap where scrolled-away
          columns/rows bleed past the frozen header + first column. */}
      <div className={cn('px-5 py-4', expanded && 'flex min-h-0 flex-1 flex-col')}>
        {toolbar && <div className="pb-3">{toolbar}</div>}
        <div
          className={cn(
            'overflow-auto',
            expanded ? 'min-h-0 flex-1' : innerScroll && 'max-h-[70vh]',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Table cell content. Rich nodes (links, expand buttons) can carry an
 * explicit `sort` value used for column sorting and CSV export.
 */
export type LegacyCell = React.ReactNode | { node: React.ReactNode; sort: string | number };

const isRichCell = (cell: LegacyCell): cell is { node: React.ReactNode; sort: string | number } =>
  typeof cell === 'object' &&
  cell !== null &&
  !React.isValidElement(cell) &&
  'node' in cell &&
  'sort' in cell;

const cellNode = (cell: LegacyCell): React.ReactNode => (isRichCell(cell) ? cell.node : cell);

/** Sort key for a cell: numbers as-is, "₹1,23,456" / "12.3%" as numbers, else lowercase text. */
const sortValue = (cell: LegacyCell): number | string => {
  const raw = isRichCell(cell) ? cell.sort : cell;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const numeric = Number(raw.replace(/[₹%\s,]/g, ''));
    return raw.trim() !== '' && Number.isFinite(numeric) ? numeric : raw.toLowerCase();
  }
  return '';
};

const compareValues = (a: number | string, b: number | string): number => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'number') return -1;
  if (typeof b === 'number') return 1;
  return String(a).localeCompare(String(b));
};

const TableSkeleton = (): React.JSX.Element => (
  <div className="animate-pulse space-y-1.5 py-1" aria-hidden>
    <div className="bg-muted h-9 rounded-md" />
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="bg-muted/50 h-8 rounded-md" />
    ))}
  </div>
);

interface LegacyTableProps {
  headers: React.ReactNode[];
  /** Optional extra header row above `headers` with colSpans (grouped columns). */
  headerGroups?: Array<{ label: React.ReactNode; colSpan: number }>;
  /** Body rows - caller renders each cell (include S.No. itself if needed). */
  rows: LegacyCell[][];
  /** Optional bold row pinned FIRST (below the header), never sorted. */
  leadRow?: LegacyCell[];
  /** Optional bold totals row (always pinned last, never sorted). */
  totals?: LegacyCell[];
  loading?: boolean;
  emptyText?: string;
  /** Extra classes on the `<table>`, e.g. a caller-specific font size. */
  className?: string;
}

type SortState = { col: number; dir: 'asc' | 'desc' } | null;

const cellClass = 'border-border/60 border-r border-b px-4 py-2.5 align-middle whitespace-nowrap';
const headerCellClass = `${cellClass} bg-muted text-muted-foreground border-t text-xs font-medium tracking-wide uppercase`;

export const LegacyTable = ({
  headers,
  headerGroups,
  rows,
  leadRow,
  totals,
  loading = false,
  emptyText = 'No data in this range.',
  className,
}: LegacyTableProps): React.JSX.Element => {
  const [sort, setSort] = useState<SortState>(null);

  // Height of the grouped-header row, so the column-header row can pin directly
  // beneath it. Re-measured on resize because group labels wrap when narrow.
  const groupRowRef = useRef<HTMLTableRowElement>(null);
  const [groupHeight, setGroupHeight] = useState(0);
  useLayoutEffect(() => {
    const row = groupRowRef.current;
    if (!row) {
      setGroupHeight(0);
      return;
    }
    const measure = (): void => setGroupHeight(row.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
    // `loading` / row count matter: the table (and this ref) only mounts once
    // the skeleton is replaced, so measuring must re-run at that point.
  }, [headerGroups?.length, loading, rows.length]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const direction = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort(
      (a, b) => direction * compareValues(sortValue(a[sort.col]), sortValue(b[sort.col])),
    );
  }, [rows, sort]);

  // For grouped tables: which column starts a new group, so we can draw a
  // stronger divider between sections and tint alternate group headers - makes
  // the "Contactability | Ghosting | ..." blocks read as distinct sections.
  const boundaryCols = useMemo(() => {
    const set = new Set<number>();
    if (headerGroups) {
      let col = 0;
      headerGroups.forEach((group, gi) => {
        if (gi > 0) set.add(col);
        col += group.colSpan;
      });
    }
    return set;
  }, [headerGroups]);
  const divider = (colIndex: number): string | undefined =>
    boundaryCols.has(colIndex) ? 'border-l-border border-l-2' : undefined;

  if (loading) return <TableSkeleton />;
  if (rows.length === 0) {
    return <p className="text-muted-foreground py-8 text-center text-sm">{emptyText}</p>;
  }

  // asc → desc → off, per column.
  const onSort = (col: number): void => {
    setSort((prev) => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
    });
  };

  // Sticky is applied per-CELL (not per-row): Chrome renders a sticky <tr>
  // inconsistently once a sticky first column is also present, which is what
  // makes the header and first column drift out of alignment on scroll. With a
  // grouped header the second row pins just below the first, offset by the
  // group row's MEASURED height rather than a fixed one, so a wrapped group
  // label pushes the metric row down instead of hiding behind it.
  return (
    <table
      className={cn('text-foreground w-full border-separate border-spacing-0 text-sm', className)}
    >
      <thead>
        {headerGroups && (
          <tr ref={groupRowRef}>
            {headerGroups.map((group, index) => {
              // Skip empty-label spacer groups (e.g. the leading Agent column).
              // Opaque tint (a fixed hsl, not an /opacity value) so the sticky
              // header never shows body rows bleeding through it on scroll. Uses
              // the theme's foreground hue (197) a few steps darker than the
              // near-white `bg-muted` base, so alternate groups read as a
              // distinct dark-grey band without washing out the header text.
              const tint = group.label && index % 2 === 1 ? 'bg-gray-200' : undefined;
              return (
                <th
                  key={index}
                  colSpan={group.colSpan}
                  className={cn(
                    headerCellClass,
                    'sticky top-0 h-10 text-center',
                    index === 0 ? 'left-0 z-30 border-l' : 'z-20',
                    index > 0 && group.label ? 'border-l-border border-l-2' : undefined,
                    tint,
                  )}
                >
                  {group.label}
                </th>
              );
            })}
          </tr>
        )}
        <tr>
          {headers.map((header, index) => {
            const active = sort?.col === index;
            const SortIcon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
            return (
              <th
                key={index}
                onClick={() => onSort(index)}
                aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                // Grouped tables pin this row BELOW the group row, offset by the
                // group row's measured height so the two stack instead of
                // overlapping. Measured rather than hard-coded because the group
                // labels wrap at narrow widths.
                style={{ top: groupHeight }}
                className={cn(
                  headerCellClass,
                  'sticky cursor-pointer text-left select-none',
                  headerGroups && 'border-t-0',
                  index === 0 ? 'left-0 z-30 border-l' : 'z-20',
                  divider(index),
                )}
              >
                <span className="flex items-center gap-1">
                  {header}
                  <SortIcon
                    className={cn('h-3 w-3 shrink-0', active ? 'text-foreground' : 'opacity-40')}
                  />
                </span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {leadRow && (
          <tr className="font-semibold">
            {leadRow.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={cn(
                  cellClass,
                  'bg-muted tabular-nums',
                  cellIndex === 0 ? 'sticky left-0 z-20 border-l' : undefined,
                  divider(cellIndex),
                )}
              >
                {cellNode(cell)}
              </td>
            ))}
          </tr>
        )}
        {sortedRows.map((row, rowIndex) => (
          <tr key={rowIndex} className="hover:bg-muted/40 group">
            {row.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={cn(
                  cellClass,
                  'tabular-nums',
                  cellIndex === 0 &&
                    'bg-card group-hover:bg-muted sticky left-0 z-10 border-l transition-colors',
                  divider(cellIndex),
                )}
              >
                {cellNode(cell)}
              </td>
            ))}
          </tr>
        ))}
        {totals && (
          <tr className="font-semibold">
            {totals.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={cn(
                  cellClass,
                  'bg-muted sticky bottom-0 tabular-nums',
                  cellIndex === 0 ? 'left-0 z-30 border-l' : 'z-20',
                  divider(cellIndex),
                )}
              >
                {cellNode(cell)}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
};
